// Centralized bilingual content — Book & Smile.

export const BRAND = "Book & Smile";
export const TAGLINE_EN = "Your trusted person in France.";
export const TAGLINE_FR = "Votre personne de confiance en France.";
export const WHATSAPP_NUMBER = "33746759648";
export const wa = (msg) =>
  `https://wa.me/${WHATSAPP_NUMBER}${msg ? `?text=${encodeURIComponent(msg)}` : ""}`;

export const LEGAL_IDENTITY = {
  commercialName: "Book & Smile",
  legalName: "Book & Smile",
  legalForm: "Entreprise individuelle (EI)",
  rcsCity: "Melun",
  rcsNumber: "992 697 888",
  siren: "992 697 888",
  headquartersAddress: "16 Rue de la Gare, 77210 Avon, France",
  operator: "Zollo Ngam Michel Junior",
  operatorLabel: "Entreprise individuelle exploitée par",
  director: "Zollo Ngam Michel Junior",
  directorRole: "Exploitant",
  registrationDate: "16 octobre 2025",
  vatStatus: "Non assujetti à la TVA – art. 293 B du CGI",
  contactEmail: "hello@bookandsmile.fr",
  contactPhone: "+33 7 46 75 96 48",
};

export const LEGAL_FOOTER_LINE_EN = `${LEGAL_IDENTITY.legalName} — ${LEGAL_IDENTITY.operatorLabel} ${LEGAL_IDENTITY.operator} · Trading as ${LEGAL_IDENTITY.commercialName} · RCS ${LEGAL_IDENTITY.rcsCity} ${LEGAL_IDENTITY.rcsNumber} · Registered office: ${LEGAL_IDENTITY.headquartersAddress}.`;
export const LEGAL_FOOTER_LINE_FR = `${LEGAL_IDENTITY.legalName} — ${LEGAL_IDENTITY.operatorLabel} ${LEGAL_IDENTITY.operator} · Nom commercial : ${LEGAL_IDENTITY.commercialName} · RCS ${LEGAL_IDENTITY.rcsCity} : ${LEGAL_IDENTITY.rcsNumber} · Siège social : ${LEGAL_IDENTITY.headquartersAddress}.`;

export const LEGAL_DISCLAIMER_EN =
  "Book & Smile is a private assistance and executive support practice. We do not provide regulated legal, tax, accounting or real estate brokerage services. Where regulated advice is required, we coordinate with the licensed professionals of your choice.";
export const LEGAL_DISCLAIMER_FR =
  "Book & Smile est un cabinet d'assistance privée et de support exécutif. Nous ne fournissons aucun service réglementé de conseil juridique, fiscal, comptable ou d'intermédiation immobilière. Lorsqu'un conseil réglementé est requis, nous coordonnons les professionnels agréés de votre choix.";

const EN = {
  nav: {
    services: "Services",
    business: "For Business",
    chauffeur: "Chauffeur",
    howItWorks: "How it works",
    membership: "Membership",
    journal: "Insights",
    about: "About",
    contact: "Contact",
    headerCta: "Request a Private Consultation",
    mobileCta: "WhatsApp",
  },
  home: {
    waMsgPrimary: "Hello, I would like to request a private consultation with Book & Smile.",
    eyebrow: "Book & Smile · Private Concierge & Personal Assistant · Paris",
    h1a: "Your private concierge",
    h1b: "in Paris.",
    tagline: "Consider it handled.",
    answer:
      "Discreet personal, executive and lifestyle assistance for international clients. From everyday arrangements to complex requests, Book & Smile takes care of the details so you can focus on what matters.",
    heroMeta: "Paris · France · International Clients · English & French",
    commercialTitle: "Paris is easier when someone reliable is handling the details.",
    commercialP1:
      "Your time is valuable. Whether you are visiting Paris, managing a business, settling in for a season, hosting important guests or simply need someone you can trust on the ground, Book & Smile provides discreet assistance tailored to your needs.",
    commercialSignature: "You ask. We handle it.",
    ctaPrimary: "Tell us what you need in Paris.",
    ctaSecondary: "Explore our services",
    servicesEyebrow: "Four services. One trusted point of contact.",
    servicesTitle: "What we handle",
    advantageEyebrow: "The Book & Smile difference",
    advantageTitle: "One trusted point of contact in Paris.",
    advantageText:
      "Instead of a driver, a restaurant contact, an assistant, someone for errands, someone to organise an event and someone to fix a problem — you have Book & Smile. One number. One person. Consider it handled.",
    whoEyebrow: "Who we serve",
    whoTitle: "Designed for people who value their time.",
    whoItems: [
      { title: "International Executives", text: "When your schedule doesn't allow you to manage every detail yourself." },
      { title: "Entrepreneurs & Business Owners", text: "When Paris requires someone on the ground you can trust." },
      { title: "International Visitors", text: "When you want to experience Paris without dealing with the logistics." },
      { title: "Families & Private Clients", text: "When everyday life requires discreet and reliable assistance." },
    ],
    membershipTeaserEyebrow: "How we work",
    membershipTeaserTitle: "One-off assistance, or a monthly private assistant.",
    membershipTeaserText:
      "A single request handled end to end, or ongoing assistance at three levels — Essential, Executive and Private Office. No hourly billing, no ticketing. One point of contact.",
    membershipTeaserCta: "View membership",
    founderEyebrow: "The founder",
    founderTitle: "A person, not a platform.",
    founderText: "Book & Smile was founded by Michel Zollo — a bilingual professional trained in visa administration and formerly of INSEAD's administrative staff in Fontainebleau. Years of serving international executives, diplomats and families taught one lesson: what clients value most is the certainty that when they ask, it is handled.",
    founderLink: "About Book & Smile",
    faqTitle: "Questions, answered directly",
    faqs: [
      { q: "What does a Book & Smile personal assistant handle?", a: "Personal, executive and lifestyle matters in France: appointments, reservations, errands, business logistics, guest management, events and the requests that fit no category — handled by one named, bilingual person." },
      { q: "Do you work in English?", a: "Entirely. Native French and fluent English — you instruct in English, everything France-facing happens natively in French." },
      { q: "How does the engagement work?", a: "One-off assistance for a single request, or monthly membership (Essential, Executive, Private Office). Terms are defined in a private consultation." },
      { q: "How is confidentiality handled?", a: "NDA by default for any client who requests one, a single named person on your file, and no client is ever named — publicly or privately." },
    ],
    trustEyebrow: "The standard",
    trustTitle: "Discretion is the operating norm.",
    trustItems: [
      { title: "NDA by default", text: "Confidentiality agreements are signed by default for any client who requests them — and observed rigorously in their absence." },
      { title: "One interlocutor", text: "You never repeat yourself. One bilingual person owns every request from instruction to completion." },
      { title: "Written follow-through", text: "Every request is acknowledged, tracked and closed in writing. Nothing is left open." },
    ],
  },
  services: {
    items: [
      {
        id: "private-assistance",
        route: "/personal-assistant-paris",
        eyebrow: "01 — Personal",
        title: "Personal Assistance",
        text: "Personal support for the things that require time, organisation or local knowledge — reservations, appointments, shopping & sourcing, deliveries, personal errands, special requests.",
        cta: "Personal Assistant in Paris",
      },
      {
        id: "executive-assistance",
        route: "/executive-assistant-paris",
        eyebrow: "02 — Executive",
        title: "Executive Assistance",
        text: "Support for executives, entrepreneurs and international professionals — meeting arrangements, travel coordination, business logistics, event organisation, guest management, local representation.",
        cta: "Executive Assistant in Paris",
      },
      {
        id: "lifestyle-concierge",
        route: "/lifestyle-concierge-paris",
        eyebrow: "03 — Lifestyle",
        title: "Lifestyle Concierge",
        text: "Access and assistance for everyday life in Paris — restaurants, cultural experiences, events, shopping, wellness, bespoke experiences.",
        cta: "Lifestyle Concierge in Paris",
      },
      {
        id: "travel-events",
        route: "/travel-concierge-paris",
        eyebrow: "04 — Travel & Events",
        title: "Travel & Events",
        text: "Planning and coordination for Paris and beyond — itineraries, private experiences, transportation, events, VIP guest coordination, special occasions.",
        cta: "Travel & Events in Paris",
      },
    ],
  },
  servicesPage: {
    seoTitle: "Services — Personal Assistant in Paris | Book & Smile",
    seoDesc: "Book & Smile services in Paris: personal assistant, executive assistant, private concierge, lifestyle management, travel & events. One trusted point of contact.",
    eyebrow: "Services",
    title: "Four services.",
    titleB: "One trusted point of contact.",
    intro:
      "Everything Book & Smile handles falls under four services. Whatever the request, you speak to the same person — and it comes back done, confirmed in writing.",
    moreEyebrow: "Explore further",
    moreTitle: "Dedicated pages by need",
    moreLinks: [
      { to: "/personal-assistant-paris", label: "Personal Assistant in Paris" },
      { to: "/private-concierge-paris", label: "Private Concierge in Paris" },
      { to: "/luxury-concierge-paris", label: "Luxury Concierge in Paris" },
      { to: "/restaurant-concierge-paris", label: "Restaurant Concierge in Paris" },
      { to: "/private-chauffeur-paris", label: "Private Chauffeur in Paris" },
      { to: "/luxury-experiences-paris", label: "Luxury Experiences in Paris" },
      { to: "/business-travel-concierge-paris", label: "Business Travel Concierge" },
      { to: "/last-minute-concierge-paris", label: "Last-Minute Concierge" },
      { to: "/personal-shopper-paris", label: "Personal Shopper in Paris" },
      { to: "/corporate-concierge-paris", label: "Corporate Concierge in Paris" },
      { to: "/personal-assistant-for-international-clients", label: "Personal Assistant for International Clients" },
      { to: "/executive-assistant-france", label: "Executive Assistant in France" },
      { to: "/personal-assistant-international-families", label: "For International Families" },
    ],
  },
  membership: {
    seoTitle: "Membership — Personal Assistant on Retainer | Book & Smile",
    seoDesc: "One-off assistance or a personal assistant on monthly retainer in Paris — Essential, Executive and Private Office. Terms set in a private consultation.",
    eyebrow: "Membership",
    title: "One point of contact.",
    titleB: "Everything handled.",
    intro:
      "We work in two ways: one-off assistance for a single request, and monthly membership for ongoing needs. No hourly billing, no ticketing systems. You choose a level of coverage; we assign you a single bilingual point of contact.",
    tiers: [
      {
        id: "essential",
        name: "Essential",
        desc: "For a few hours a month. Personal requests handled with care — reservations, errands, appointments, sourcing.",
        features: [
          "Private assistance for personal requests",
          "Reservations, appointments & errands",
          "One bilingual point of contact",
          "Everything confirmed in writing",
          "Response within one business day",
        ],
        cta: "Request a Private Consultation",
      },
      {
        id: "executive",
        name: "Executive",
        highlight: true,
        desc: "Regular assistance. Personal and executive support for professionals operating in Paris.",
        features: [
          "Everything in Essential",
          "Meeting arrangements & travel coordination",
          "Business logistics & guest management",
          "Local representation in Paris",
          "Priority response, same business day",
        ],
        cta: "Request a Private Consultation",
      },
      {
        id: "private-office",
        name: "Private Office",
        desc: "Priority, highly personalised service. Full coverage for your family, your business and your life in Paris.",
        features: [
          "Everything in Executive",
          "Lifestyle concierge & bespoke experiences",
          "Travel & events coordination",
          "Household & family coordination",          "NDA by default · extended availability",
        ],
        cta: "Request a Private Consultation",
      },
    ],
    note: "Pricing is defined in a private consultation, according to scope and level of service. One-time requests are also welcome.",
    faqTitle: "Membership, briefly",
    faqs: [
      { q: "Do you accept one-time requests?", a: "Yes. One-off assistance is quoted per request. Many members begin with a single request and move to membership once they see how the desk works." },
      { q: "What is not included?", a: "Third-party costs (suppliers, tickets, couriers) are re-invoiced at cost, with your prior approval. Regulated professional services come from licensed professionals we coordinate." },
      { q: "Can a company pay for the service?", a: "Yes. Corporate arrangements with monthly reporting are common for Executive and Private Office levels." },
    ],
  },
  howItWorks: {
    seoTitle: "How It Works — One Trusted Person in France | Book & Smile",
    seoDesc: "How Book & Smile works: a private conversation, a defined scope, a single bilingual interlocutor. You ask. We handle it.",
    eyebrow: "How it works",
    title: "Simple by design.",
    intro:
      "The model is deliberately simple: one person, one channel, everything in writing. Here is exactly what happens.",
    steps: [
      { n: "01", title: "A private conversation", text: "We begin with a confidential call or meeting. You describe how you live and work in France; we listen and identify where the friction is." },
      { n: "02", title: "A defined scope", text: "You receive a written proposal: membership level, scope, and your dedicated interlocutor. No surprises, no small print." },
      { n: "03", title: "One channel", text: "From day one you use a single channel — WhatsApp, email or phone. Every request is acknowledged and tracked in writing." },
      { n: "04", title: "Handled, and confirmed", text: "Each matter is closed with a written confirmation. Monthly, you receive a discreet summary of everything handled." },
    ],
    ctaTitle: "Begin with a conversation.",
    ctaText: "Introductions are private and without obligation.",
  },
  about: {
    seoTitle: "About Book & Smile — Personal Assistant House, Paris",
    seoDesc: "Book & Smile is a private assistance and executive support house serving international clients in Paris and across France. Founded by Michel Zollo.",
    eyebrow: "About",
    title: "A house built on trust,",
    titleB: "not on volume.",
    p1: "Book & Smile exists for one reason: international clients in France need one person they can trust completely — not a marketplace, not a call centre, not an app.",
    p2: "The house was founded by Michel Zollo, a bilingual professional trained in visa administration and formerly of INSEAD's administrative staff in Fontainebleau. Years of serving international executives, diplomats and families taught one lesson: what clients value most is not a service catalogue. It is the certainty that when they ask, it is handled.",
    p3: "The desk deliberately serves a limited number of members. Every engagement is personal, every request is owned end to end, and discretion is the operating norm — with NDAs signed by default for any client who requests them.",
    valuesTitle: "What we believe",
    values: [
      { title: "Conclusion first", text: "You get the answer, then the detail. Your time is the scarcest resource we manage." },
      { title: "Ownership", text: "One person is responsible for your entire file. Accountability is never diluted." },
      { title: "Discretion", text: "Who our members are, and what we handle for them, is nobody's business but theirs." },
    ],
  },
  contact: {
    seoTitle: "Contact — Request a Private Consultation | Book & Smile",
    seoDesc: "Request a private consultation with Book & Smile — your personal assistant and private concierge in Paris. One-time requests, monthly retainers.",
    eyebrow: "Contact",
    titleA: "Request a",
    titleB: "private consultation.",
    intro:
      "Tell us briefly what you need help with in Paris. We reply personally, within one business day.",
    waMsg: "Hello, I would like to request a private consultation with Book & Smile.",
    waCta: "WhatsApp the desk",
    hoursLabel: "Availability",
    hoursLine1: "Every day, 8:00 – 20:00 (Paris time)",
    hoursLine2: "Members: 24/7",
    basedLabel: "Based",
    basedLine: "Paris & Fontainebleau, France",
    waLabel: "Direct line",
    waNumber: "+33 7 46 75 96 48",
    nameLabel: "Name",
    namePh: "Your name",
    emailLabel: "Email",
    emailPh: "you@example.com",
    serviceLabel: "What do you need help with?",
    servicePh: "Select a service",
    serviceOther: "Other / not sure yet",
    phoneLabel: "Phone / WhatsApp",
    phonePh: "+1 …",
    timingLabel: "When do you need assistance?",
    timingPh: "e.g. This week · 15–20 July · Ongoing",
    frequencyLabel: "Type of assistance",
    frequencyOptions: ["One-time request", "Regular assistance", "Corporate"],
    countryLabel: "Where are you based?",
    countryPh: "Select a country",
    countryOther: "Rest of the world",
    countryOptions: ["France", "United States", "Canada", "United Kingdom", "Germany", "Switzerland", "China", "South Korea", "Brazil", "Argentina", "Panama", "Russia", "Turkey", "United Arab Emirates", "Nigeria", "South Africa", "Namibia"],
    messageLabel: "Message",
    messagePh: "Briefly: who you are, and what you would like handled in France.",
    privacy: "Your message is confidential. It is read by one person and never shared.",
    send: "Send request",
    sending: "Sending…",
    sent: "Sent",
    successToast: "Received. We reply personally within one business day.",
    errorToastFields: "Please complete your name, email and message.",
    errorToastNetwork: "Could not send. Please try WhatsApp instead.",
  },
  faq: {
    seoTitle: "FAQ — Personal Assistant & Private Concierge | Book & Smile",
    seoDesc: "Answers on how Book & Smile works in Paris: membership, confidentiality, languages, scope of assistance and how to become a member.",
    eyebrow: "FAQ",
    title: "Questions, answered directly.",
    items: [
      { q: "What exactly is Book & Smile?", a: "A private assistance and executive support practice. International clients retain one trusted, bilingual person in France who handles their personal, business and lifestyle matters end to end." },
      { q: "Who do you work with?", a: "International executives, entrepreneurs, diplomats, investors and families who live in France, are moving to France, or manage interests here from abroad. Engagements begin by introduction." },
      { q: "Is this a concierge service?", a: "Concierge access is one of four desks. The core is broader: a private assistant who also handles administration, business support and corporate matters — not only reservations." },
      { q: "How does confidentiality work?", a: "NDAs are signed by default for any client who requests them. Internally, your file is handled by one person. We never reference members by name, publicly or privately." },
      { q: "Do you work in English?", a: "Entirely. Native French and fluent English. Every document, call and appointment can be handled bilingually." },
      { q: "How fast do you respond?", a: "Private members: within one business day. Executive and Private Office members: same business day, with extended availability for urgent matters." },
      { q: "Do you provide legal or tax advice?", a: "No. Regulated advice comes from licensed professionals. We prepare, coordinate and follow through with your lawyers, accountants and notaries — or introduce vetted ones." },
      { q: "How do I become a member?", a: "Request an introduction via the contact page or WhatsApp. A private conversation, a written proposal, and the desk opens your file. Three-month minimum term." },
    ],
  },
  journal: {
    seoTitle: "The Journal — Personal Assistant Insights | Book & Smile",
    seoDesc: "Practical briefings on personal assistants, private concierge, lifestyle management and French administration for international clients in Paris.",
    eyebrow: "The Journal",
    title: "Briefings from the desk.",
    intro:
      "Practical, conclusion-first briefings on living, working and operating in France — written for international clients, not for search engines.",
    soon: "In preparation",
    articles: [
      { slug: "hire-private-assistant-france-guide", title: "How to Hire a Private Assistant in France: The Complete Guide", abstract: "Retainer vs. hourly, employee vs. practice, and the questions that actually predict a good fit." },
      { slug: "private-assistant-vs-concierge", title: "Private Assistant vs. Concierge: Which Do You Actually Need?", abstract: "The two models solve different problems. Choosing wrong costs a year." },
      { slug: "cost-of-no-support-france", title: "The Real Cost of Operating in France Without Support", abstract: "Missed deadlines, penalty interest and lost weeks — quantified." },
      { slug: "executive-support-international-leaders", title: "Executive Support in France: How International Leaders Stay Operational", abstract: "What changes when your EA does not speak French — and how to fix it." },
      { slug: "setting-up-business-france-administrative-reality", title: "Setting Up a Business in France: The Administrative Reality", abstract: "Kbis, URSSAF, banking — the sequence that works, and the one that stalls." },
      { slug: "french-administration-decoded", title: "The French Administration, Decoded: CPAM, URSSAF, Impôts", abstract: "A field map of the institutions that will govern your French life." },
      { slug: "paris-international-families", title: "Paris for International Families: Schools, Healthcare, Daily Life", abstract: "The decisions that matter in the first ninety days, in order." },
      { slug: "trusted-person-every-country", title: "Why Global Families Keep a Trusted Person in Every Country", abstract: "The quiet infrastructure behind families who move without friction." },
      { slug: "discretion-standard-ndas-privacy", title: "The Discretion Standard: NDAs, Privacy and Private Clients", abstract: "What professional discretion actually obliges — and how to verify it." },
      { slug: "first-30-days-france", title: "From Airport to Operational: Your First 30 Days in France", abstract: "A conclusion-first checklist for arriving and being functional fast." },
    ],
  },
  testimonials: {
    eyebrow: "Words from members",
    title: "Our members are never named.",
    note: "Quotes published with permission. Identities withheld by principle.",
    items: [
      {
        quote: "We arrived with two children, a company to run and no French. Within a month, nobody in the family was thinking about the infrastructure anymore.",
        who: "An international executive family",
        where: "Paris 16e",
      },
      {
        quote: "My assistant in London finally has a counterpart in Paris. Instructions go out in English; everything comes back done, in writing.",
        who: "A founder",
        where: "London & Paris",
      },
      {
        quote: "The préfecture, the bank, the school — one message, and it is handled. I did not believe it until I saw it.",
        who: "A private client",
        where: "Neuilly-sur-Seine",
      },
    ],
  },
  finalCta: {
    title: "Consider it handled.",
    text: "One trusted point of contact in Paris. Request a private consultation.",
    cta: "Request a Private Consultation",
  },
  footer: {
    desc: "Book & Smile — your trusted person in France. Private assistance, executive support, corporate coordination and lifestyle access for international clients. By introduction.",
    servicesLabel: "Services",
    pagesLabel: "The desk",
    hoursLabel: "Availability",
    hours1: "7 days a week · 8:00 – 20:00",
    hours2: "Paris time (CET)",
    hours3: "Members: 24/7",
    hours4: "By introduction",
    legalLabel: "Legal notice",
    legalDisclaimer: LEGAL_DISCLAIMER_EN,
    legalIdentityLine: LEGAL_FOOTER_LINE_EN,
    legalNoticeLink: "Legal notice",
    rights: "Book & Smile. All rights reserved.",
    motto: "Your trusted person in France.",
  },
};

const FR = {
  nav: {
    services: "Services",
    business: "Entreprises",
    chauffeur: "Chauffeur",
    howItWorks: "Comment ça marche",
    membership: "Adhésion",
    journal: "Insights",
    about: "À propos",
    contact: "Contact",
    headerCta: "Demander une consultation privée",
    mobileCta: "WhatsApp",
  },
  home: {
    waMsgPrimary: "Bonjour, je souhaite demander une consultation privée avec Book & Smile.",
    eyebrow: "Book & Smile · Conciergerie privée & assistant personnel · Paris",
    h1a: "Votre conciergerie privée",
    h1b: "à Paris.",
    tagline: "Consider it handled.",
    answer:
      "Une assistance personnelle, exécutive et art de vivre, discrète, pour une clientèle internationale. Des arrangements du quotidien aux demandes complexes, Book & Smile s'occupe des détails pour que vous puissiez vous concentrer sur l'essentiel.",
    heroMeta: "Paris · France · Clientèle internationale · Français & Anglais",
    commercialTitle: "Paris est plus simple quand quelqu'un de fiable s'occupe des détails.",
    commercialP1:
      "Votre temps est précieux. Que vous visitiez Paris, dirigiez une entreprise, vous installiez temporairement, receviez des invités importants ou ayez simplement besoin de quelqu'un de confiance sur place, Book & Smile offre une assistance discrète, adaptée à vos besoins.",
    commercialSignature: "Vous demandez. Nous gérons.",
    ctaPrimary: "Dites-nous ce dont vous avez besoin à Paris.",
    ctaSecondary: "Découvrir nos services",
    servicesEyebrow: "Quatre services. Un seul point de contact de confiance.",
    servicesTitle: "Ce que nous gérons",
    advantageEyebrow: "La différence Book & Smile",
    advantageTitle: "Un seul point de contact de confiance à Paris.",
    advantageText:
      "Au lieu d'un chauffeur, d'un contact restaurant, d'un assistant, d'une personne pour les courses, de quelqu'un pour organiser un événement et de quelqu'un pour régler un problème — vous avez Book & Smile. Un numéro. Une personne. Consider it handled.",
    whoEyebrow: "Pour qui",
    whoTitle: "Conçu pour ceux qui valorisent leur temps.",
    whoItems: [
      { title: "Dirigeants internationaux", text: "Quand votre agenda ne vous permet pas de gérer chaque détail vous-même." },
      { title: "Entrepreneurs & chefs d'entreprise", text: "Quand Paris exige quelqu'un de confiance sur place." },
      { title: "Visiteurs internationaux", text: "Quand vous voulez vivre Paris sans en subir la logistique." },
      { title: "Familles & clients privés", text: "Quand le quotidien exige une assistance discrète et fiable." },
    ],
    membershipTeaserEyebrow: "Comment nous travaillons",
    membershipTeaserTitle: "Une demande ponctuelle, ou un assistant privé mensuel.",
    membershipTeaserText:
      "Une demande unique gérée de bout en bout, ou une assistance continue à trois niveaux — Essential, Executive et Private Office. Pas de facturation horaire, pas de tickets. Un point de contact.",
    membershipTeaserCta: "Voir l'adhésion",
    founderEyebrow: "Le fondateur",
    founderTitle: "Une personne, pas une plateforme.",
    founderText: "Book & Smile a été fondé par Michel Zollo — professionnel bilingue formé à l'administration des visas et ancien membre du personnel administratif de l'INSEAD à Fontainebleau. Des années au service de dirigeants, diplomates et familles internationales ont enseigné une leçon : ce que les clients valorisent le plus, c'est la certitude que lorsqu'ils demandent, c'est géré.",
    founderLink: "À propos de Book & Smile",
    faqTitle: "Des questions, des réponses directes",
    faqs: [
      { q: "Que gère un assistant personnel Book & Smile ?", a: "Vos affaires personnelles, exécutives et art de vivre en France : rendez-vous, réservations, courses, logistique business, gestion d'invités, événements — portés par une personne nommée et bilingue." },
      { q: "Travaillez-vous en anglais ?", a: "Entièrement. Français natif et anglais courant — vous donnez vos instructions en anglais, tout ce qui est tourné vers la France se fait en français." },
      { q: "Comment fonctionne l'engagement ?", a: "Assistance ponctuelle pour une demande unique, ou adhésion mensuelle (Essential, Executive, Private Office). Conditions définies en consultation privée." },
      { q: "Comment la confidentialité est-elle gérée ?", a: "NDA par défaut pour tout client qui le demande, une seule personne nommée sur votre dossier, et aucun client n'est jamais cité — publiquement ou en privé." },
    ],
    trustEyebrow: "Le standard",
    trustTitle: "La discrétion est la norme.",
    trustItems: [
      { title: "NDA par défaut", text: "Des accords de confidentialité sont signés par défaut pour tout client qui le demande — et observés rigoureusement en leur absence." },
      { title: "Un interlocuteur", text: "Vous ne vous répétez jamais. Une personne bilingue porte chaque demande, de l'instruction à l'exécution." },
      { title: "Suivi écrit", text: "Chaque demande est accusée, suivie et clôturée par écrit. Rien ne reste ouvert." },
    ],
  },
  services: {
    items: [
      {
        id: "private-assistance",
        route: "/personal-assistant-paris",
        eyebrow: "01 — Personnel",
        title: "Assistance Personnelle",
        text: "Un soutien personnel pour tout ce qui demande du temps, de l'organisation ou une connaissance locale — réservations, rendez-vous, achats & sourcing, livraisons, courses personnelles, demandes spéciales.",
        cta: "Assistant personnel à Paris",
      },
      {
        id: "executive-assistance",
        route: "/executive-assistant-paris",
        eyebrow: "02 — Exécutif",
        title: "Assistance Exécutive",
        text: "Un soutien pour dirigeants, entrepreneurs et professionnels internationaux — organisation de réunions, coordination de déplacements, logistique business, événements, gestion d'invités, représentation locale.",
        cta: "Executive assistant à Paris",
      },
      {
        id: "lifestyle-concierge",
        route: "/lifestyle-concierge-paris",
        eyebrow: "03 — Art de vivre",
        title: "Conciergerie Lifestyle",
        text: "Accès et assistance pour la vie quotidienne à Paris — restaurants, expériences culturelles, événements, shopping, bien-être, expériences sur mesure.",
        cta: "Conciergerie lifestyle à Paris",
      },
      {
        id: "travel-events",
        route: "/travel-concierge-paris",
        eyebrow: "04 — Voyages & Événements",
        title: "Voyages & Événements",
        text: "Planification et coordination pour Paris et au-delà — itinéraires, expériences privées, transports, événements, coordination d'invités VIP, occasions spéciales.",
        cta: "Voyages & événements à Paris",
      },
    ],
  },
  servicesPage: {
    seoTitle: "Services — Assistant personnel à Paris | Book & Smile",
    seoDesc: "Les services Book & Smile à Paris : assistant personnel, assistant de direction, conciergerie privée, lifestyle management, voyages & événements. Un seul contact.",
    eyebrow: "Services",
    title: "Quatre services.",
    titleB: "Un seul point de contact de confiance.",
    intro:
      "Tout ce que Book & Smile gère relève de quatre services. Quelle que soit la demande, vous parlez à la même personne — et cela revient fait, confirmé par écrit.",
    moreEyebrow: "Aller plus loin",
    moreTitle: "Pages dédiées par besoin",
    moreLinks: [
      { to: "/personal-assistant-paris", label: "Personal assistant à Paris" },
      { to: "/private-concierge-paris", label: "Conciergerie privée à Paris" },
      { to: "/luxury-concierge-paris", label: "Conciergerie de luxe à Paris" },
      { to: "/restaurant-concierge-paris", label: "Conciergerie restaurants à Paris" },
      { to: "/private-chauffeur-paris", label: "Chauffeur privé à Paris" },
      { to: "/luxury-experiences-paris", label: "Expériences de luxe à Paris" },
      { to: "/business-travel-concierge-paris", label: "Conciergerie voyage d'affaires" },
      { to: "/last-minute-concierge-paris", label: "Conciergerie de dernière minute" },
      { to: "/personal-shopper-paris", label: "Personal shopper à Paris" },
      { to: "/corporate-concierge-paris", label: "Conciergerie d'entreprise à Paris" },
      { to: "/personal-assistant-for-international-clients", label: "Assistant personnel pour clients internationaux" },
      { to: "/executive-assistant-france", label: "Executive assistant en France" },
      { to: "/personal-assistant-international-families", label: "Pour familles internationales" },
    ],
  },
  membership: {
    seoTitle: "Adhésion — Assistant personnel au forfait, Paris | Book & Smile",
    seoDesc: "Assistance ponctuelle ou assistant personnel au forfait mensuel à Paris — niveaux Essential, Executive et Private Office. Conditions définies en consultation privée.",
    eyebrow: "Adhésion",
    title: "Un point de contact.",
    titleB: "Tout est géré.",
    intro:
      "Nous travaillons de deux façons : l'assistance ponctuelle pour une demande unique, et l'adhésion mensuelle pour les besoins réguliers. Pas de facturation horaire, pas de tickets. Vous choisissez un niveau de couverture ; nous vous attribuons un point de contact bilingue unique.",
    tiers: [
      {
        id: "essential",
        name: "Essential",
        desc: "Pour quelques heures par mois. Vos demandes personnelles gérées avec soin — réservations, courses, rendez-vous, sourcing.",
        features: [
          "Assistance privée pour demandes personnelles",
          "Réservations, rendez-vous & courses",
          "Un point de contact bilingue",
          "Tout est confirmé par écrit",
          "Réponse sous un jour ouvré",
        ],
        cta: "Demander une consultation privée",
      },
      {
        id: "executive",
        name: "Executive",
        highlight: true,
        desc: "Une assistance régulière. Soutien personnel et exécutif pour les professionnels opérant à Paris.",
        features: [
          "Tout Essential inclus",
          "Organisation de réunions & coordination de déplacements",
          "Logistique business & gestion d'invités",
          "Représentation locale à Paris",
          "Réponse prioritaire, le jour même",
        ],
        cta: "Demander une consultation privée",
      },
      {
        id: "private-office",
        name: "Private Office",
        desc: "Un service prioritaire et très personnalisé. Couverture complète pour votre famille, votre activité et votre vie à Paris.",
        features: [
          "Tout Executive inclus",
          "Conciergerie lifestyle & expériences sur mesure",
          "Coordination voyages & événements",
          "Coordination maison & famille",
          "NDA par défaut · disponibilité étendue",
        ],
        cta: "Demander une consultation privée",
      },
    ],
    note: "Les tarifs sont définis en consultation privée, selon le périmètre et le niveau de service. Les demandes ponctuelles sont également bienvenues.",
    faqTitle: "L'adhésion, en bref",
    faqs: [
      { q: "Acceptez-vous les demandes ponctuelles ?", a: "Oui. L'assistance ponctuelle est devisée à la demande. Beaucoup de membres commencent par une demande unique avant de passer à l'adhésion." },
      { q: "Qu'est-ce qui n'est pas inclus ?", a: "Les coûts tiers (prestataires, billets, coursiers) sont refacturés au réel, avec votre accord préalable. Les services réglementés relèvent de professionnels agréés que nous coordonnons." },
      { q: "Une société peut-elle payer le service ?", a: "Oui. Les accords corporate avec reporting mensuel sont courants aux niveaux Executive et Private Office." },
    ],
  },
  howItWorks: {
    seoTitle: "Comment ça marche — Votre personne de confiance | Book & Smile",
    seoDesc: "Comment fonctionne Book & Smile : une conversation privée, un périmètre défini, un interlocuteur bilingue unique. Vous demandez. Nous gérons.",
    eyebrow: "Comment ça marche",
    title: "Simple par conception.",
    intro:
      "Le modèle est délibérément simple : une personne, un canal, tout par écrit. Voici exactement ce qui se passe.",
    steps: [
      { n: "01", title: "Une conversation privée", text: "Nous commençons par un appel ou un rendez-vous confidentiel. Vous décrivez votre vie et votre activité en France ; nous identifions les frictions." },
      { n: "02", title: "Un périmètre défini", text: "Vous recevez une proposition écrite : niveau d'adhésion, périmètre et votre interlocuteur dédié. Sans surprise, sans petites lignes." },
      { n: "03", title: "Un canal unique", text: "Dès le premier jour, un seul canal — WhatsApp, email ou téléphone. Chaque demande est accusée et suivie par écrit." },
      { n: "04", title: "Géré, et confirmé", text: "Chaque dossier est clôturé par une confirmation écrite. Chaque mois, vous recevez une synthèse discrète de tout ce qui a été géré." },
    ],
    ctaTitle: "Commencez par une conversation.",
    ctaText: "Les introductions sont privées et sans engagement.",
  },
  about: {
    seoTitle: "À propos de Book & Smile — Assistant personnel, Paris",
    seoDesc: "Book & Smile : assistance personnelle et support exécutif pour une clientèle internationale à Paris et en France. Fondée par Michel Zollo.",
    eyebrow: "À propos",
    title: "Une maison fondée sur la confiance,",
    titleB: "pas sur le volume.",
    p1: "Book & Smile existe pour une raison : les clients internationaux en France ont besoin d'une personne en qui ils peuvent avoir une confiance totale — pas d'une marketplace, pas d'un centre d'appels, pas d'une application.",
    p2: "La maison a été fondée par Michel Zollo, professionnel bilingue formé à l'administration des visas et ancien membre du personnel administratif de l'INSEAD à Fontainebleau. Des années au service de dirigeants, diplomates et familles internationales ont enseigné une leçon : ce que les clients valorisent le plus n'est pas un catalogue de services. C'est la certitude que lorsqu'ils demandent, c'est géré.",
    p3: "Le desk sert délibérément un nombre limité de membres. Chaque engagement est personnel, chaque demande est portée de bout en bout, et la discrétion est la norme — avec des NDA signés par défaut pour tout client qui le demande.",
    valuesTitle: "Nos convictions",
    values: [
      { title: "La conclusion d'abord", text: "Vous recevez la réponse, puis le détail. Votre temps est la ressource la plus rare que nous gérons." },
      { title: "La responsabilité", text: "Une seule personne est responsable de l'intégralité de votre dossier. La responsabilité n'est jamais diluée." },
      { title: "La discrétion", text: "Qui sont nos membres, et ce que nous gérons pour eux, ne regarde qu'eux." },
    ],
  },
  contact: {
    seoTitle: "Contact — Demander une consultation privée | Book & Smile",
    seoDesc: "Demandez une consultation privée avec Book & Smile — votre assistant personnel et conciergerie privée à Paris. Demandes ponctuelles, forfaits mensuels.",
    eyebrow: "Contact",
    titleA: "Demander une",
    titleB: "consultation privée.",
    intro:
      "Dites-nous brièvement ce dont vous avez besoin à Paris. Nous répondons personnellement, sous un jour ouvré.",
    waMsg: "Bonjour, je souhaite demander une consultation privée avec Book & Smile.",
    waCta: "WhatsApp au desk",
    hoursLabel: "Disponibilité",
    hoursLine1: "Tous les jours, 8h00 – 20h00 (heure de Paris)",
    hoursLine2: "Membres : 24h/24, 7j/7",
    basedLabel: "Basé",
    basedLine: "Paris & Fontainebleau, France",
    waLabel: "Ligne directe",
    waNumber: "+33 7 46 75 96 48",
    nameLabel: "Nom",
    namePh: "Votre nom",
    emailLabel: "Email",
    emailPh: "vous@exemple.com",
    serviceLabel: "De quoi avez-vous besoin ?",
    servicePh: "Sélectionnez un service",
    serviceOther: "Autre / je ne sais pas encore",
    phoneLabel: "Téléphone / WhatsApp",
    phonePh: "+33 …",
    timingLabel: "Quand avez-vous besoin d'assistance ?",
    timingPh: "ex. Cette semaine · 15–20 juillet · En continu",
    frequencyLabel: "Type d'assistance",
    frequencyOptions: ["Demande ponctuelle", "Assistance régulière", "Corporate"],
    countryLabel: "Où êtes-vous basé(e) ?",
    countryPh: "Sélectionnez un pays",
    countryOther: "Reste du monde",
    countryOptions: ["France", "États-Unis", "Canada", "Royaume-Uni", "Allemagne", "Suisse", "Chine", "Corée du Sud", "Brésil", "Argentine", "Panama", "Russie", "Turquie", "Émirats arabes unis", "Nigeria", "Afrique du Sud", "Namibie"],
    messageLabel: "Message",
    messagePh: "En bref : qui vous êtes, et ce que vous souhaitez voir géré en France.",
    privacy: "Votre message est confidentiel. Il est lu par une seule personne et jamais partagé.",
    send: "Envoyer la demande",
    sending: "Envoi…",
    sent: "Envoyé",
    successToast: "Bien reçu. Nous répondons personnellement sous un jour ouvré.",
    errorToastFields: "Merci de compléter votre nom, email et message.",
    errorToastNetwork: "Envoi impossible. Essayez WhatsApp.",
  },
  faq: {
    seoTitle: "FAQ — Assistant personnel et conciergerie | Book & Smile",
    seoDesc: "Réponses sur le fonctionnement de Book & Smile : adhésion, confidentialité, langues, périmètre et comment devenir membre.",
    eyebrow: "FAQ",
    title: "Des questions, des réponses directes.",
    items: [
      { q: "Qu'est-ce que Book & Smile, exactement ?", a: "Un cabinet d'assistance privée et de support exécutif. Les clients internationaux retiennent une personne de confiance bilingue en France qui gère leurs affaires personnelles, professionnelles et leur art de vivre, de bout en bout." },
      { q: "Avec qui travaillez-vous ?", a: "Dirigeants internationaux, entrepreneurs, diplomates, investisseurs et familles qui vivent en France, s'y installent, ou y gèrent des intérêts depuis l'étranger. Les engagements commencent sur introduction." },
      { q: "Est-ce une conciergerie ?", a: "L'accès conciergerie est l'un de nos quatre pôles. Le cœur est plus large : un assistant privé qui gère aussi l'administration, le support business et les sujets corporate — pas seulement des réservations." },
      { q: "Comment fonctionne la confidentialité ?", a: "Des NDA sont signés par défaut pour tout client qui le demande. En interne, votre dossier est traité par une seule personne. Nous ne citons jamais nos membres, publiquement ou en privé." },
      { q: "Travaillez-vous en anglais ?", a: "Entièrement. Français natif et anglais courant. Chaque document, appel et rendez-vous peut être traité de manière bilingue." },
      { q: "Quel est votre délai de réponse ?", a: "Membres Private : sous un jour ouvré. Membres Executive et Private Office : le jour même, avec disponibilité étendue pour les urgences." },
      { q: "Donnez-vous des conseils juridiques ou fiscaux ?", a: "Non. Le conseil réglementé relève des professionnels agréés. Nous préparons, coordonnons et suivons avec vos avocats, experts-comptables et notaires — ou vous en présentons." },
      { q: "Comment devenir membre ?", a: "Demandez une introduction via la page contact ou WhatsApp. Une conversation privée, une proposition écrite, et le desk ouvre votre dossier. Durée minimale de trois mois." },
    ],
  },
  journal: {
    seoTitle: "Le Journal — Assistant personnel à Paris | Book & Smile",
    seoDesc: "Briefings pratiques sur l'assistant personnel, la conciergerie privée, le lifestyle management, l'administration française et le support exécutif à Paris.",
    eyebrow: "Le Journal",
    title: "Briefings du desk.",
    intro:
      "Des briefings pratiques, conclusion d'abord, sur la vie et les affaires en France — écrits pour des clients internationaux, pas pour les moteurs de recherche.",
    soon: "En préparation",
    articles: [
      { slug: "hire-private-assistant-france-guide", title: "Recruter un assistant privé en France : le guide complet", abstract: "Forfait ou horaire, salarié ou cabinet, et les questions qui prédisent vraiment un bon choix." },
      { slug: "private-assistant-vs-concierge", title: "Assistant privé ou conciergerie : de quoi avez-vous vraiment besoin ?", abstract: "Deux modèles, deux problèmes différents. Se tromper coûte un an." },
      { slug: "cost-of-no-support-france", title: "Le vrai coût d'opérer en France sans support", abstract: "Échéances manquées, pénalités et semaines perdues — chiffrés." },
      { slug: "executive-support-international-leaders", title: "Support exécutif en France : comment les dirigeants internationaux restent opérationnels", abstract: "Ce qui change quand votre assistante ne parle pas français — et comment y remédier." },
      { slug: "setting-up-business-france-administrative-reality", title: "Créer une société en France : la réalité administrative", abstract: "Kbis, URSSAF, banque — la séquence qui fonctionne, et celle qui bloque." },
      { slug: "french-administration-decoded", title: "L'administration française, décodée : CPAM, URSSAF, Impôts", abstract: "Une carte des institutions qui gouverneront votre vie française." },
      { slug: "paris-international-families", title: "Paris pour les familles internationales : écoles, santé, quotidien", abstract: "Les décisions qui comptent dans les quatre-vingt-dix premiers jours, dans l'ordre." },
      { slug: "trusted-person-every-country", title: "Pourquoi les familles globales gardent une personne de confiance dans chaque pays", abstract: "L'infrastructure discrète des familles qui se déplacent sans friction." },
      { slug: "discretion-standard-ndas-privacy", title: "Le standard de discrétion : NDA, vie privée et clients privés", abstract: "Ce que la discrétion professionnelle oblige réellement — et comment la vérifier." },
      { slug: "first-30-days-france", title: "De l'aéroport à l'opérationnel : vos 30 premiers jours en France", abstract: "Une checklist conclusion-d'abord pour arriver et être fonctionnel vite." },
    ],
  },
  testimonials: {
    eyebrow: "Paroles de membres",
    title: "Nos membres ne sont jamais nommés.",
    note: "Citations publiées avec accord. Identités tues par principe.",
    items: [
      {
        quote: "Nous sommes arrivés avec deux enfants, une société à diriger et pas un mot de français. En un mois, plus personne dans la famille ne pensait à l'infrastructure.",
        who: "Une famille de dirigeants internationaux",
        where: "Paris 16e",
      },
      {
        quote: "Mon assistante à Londres a enfin un homologue à Paris. Les instructions partent en anglais ; tout revient fait, par écrit.",
        who: "Un fondateur",
        where: "Londres & Paris",
      },
      {
        quote: "La préfecture, la banque, l'école — un message, et c'est géré. Je n'y croyais pas avant de le voir.",
        who: "Un client privé",
        where: "Neuilly-sur-Seine",
      },
    ],
  },
  finalCta: {
    title: "Consider it handled.",
    text: "Un seul point de contact de confiance à Paris. Demandez une consultation privée.",
    cta: "Demander une consultation privée",
  },
  footer: {
    desc: "Book & Smile — votre personne de confiance en France. Assistance privée, support exécutif, coordination corporate et art de vivre pour une clientèle internationale. Sur introduction.",
    servicesLabel: "Services",
    pagesLabel: "Le desk",
    hoursLabel: "Disponibilité",
    hours1: "7 jours sur 7 · 8h00 – 20h00",
    hours2: "Heure de Paris (CET)",
    hours3: "Membres : 24h/24",
    hours4: "Sur introduction",
    legalLabel: "Mention légale",
    legalDisclaimer: LEGAL_DISCLAIMER_FR,
    legalIdentityLine: LEGAL_FOOTER_LINE_FR,
    legalNoticeLink: "Mentions légales",
    rights: "Book & Smile. Tous droits réservés.",
    motto: "Votre personne de confiance en France.",
  },
};

export const TRANSLATIONS = { en: EN, fr: FR };
